import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiRequestOptions, ApiService } from './api.service';
import { EmitterService } from './emitter.service';
import { UserLoggedOutEvent } from '../models/events';

export interface EntityParticipant {
  username: string;
  /** IAM account uuid — the identity mention chips and drive message payloads key on. */
  accountUuid?: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  avatarUrl?: string;
}

interface RawParticipant {
  username: string;
  accountUuid?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
}

interface EntityParticipantsResponse {
  message?: string;
  data?: {
    entityUuid: string;
    entityType: string;
    participants: RawParticipant[];
  };
}

const PARTICIPANTS_CACHE_TTL_MS: number = 1 * 60 * 1000; // 1 minute

interface ParticipantCacheEntry {
  data: EntityParticipant[];
  expiresAt: number;
}

const CACHE_PARTICIPANTS: Record<string, ParticipantCacheEntry> = {};

/**
 * Unified per-entity participants listing — GET /api/v1/participants/{entityType}/{entityUuid},
 * the same endpoint the task surfaces use. Entity types follow the platform taxonomy
 * (BRAND, WORKSPACE, CAMPAIGN, PROJECT, SECTION, ... — see PLATFORM_CHAT_ENTITY_TYPE).
 * Replaces the legacy per-entity /users listings.
 */
@Injectable({ providedIn: 'root' })
export class EntityParticipantsService {
  private readonly api: ApiService = inject(ApiService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor() {
    // The root instance and the module-level cache outlive logout/login (SPA navigation,
    // no reload) — drop the previous user's participant lists on logout, same idiom as
    // PlatformRbacSignatureService.
    EmitterService.get(UserLoggedOutEvent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clearCache());
  }

  /**
   * `drive: true` routes drive-storage sub-entities so the backend resolves the owning
   * project's participants (STUDIO-1808). A failed fetch degrades to [] — but ApiService
   * error-handler side effects requested via `options` (e.g. redirectOnForbidden's overlay
   * and redirect) still fire before the error is swallowed.
   */
  public getParticipants(
    entityType: string,
    entityUuid: string,
    drive: boolean = false,
    options: ApiRequestOptions = {}
  ): Observable<EntityParticipant[]> {
    const cacheKey: string = `${entityType}_${entityUuid}${drive ? '_drive' : ''}`;
    const cached: ParticipantCacheEntry | undefined = CACHE_PARTICIPANTS[cacheKey];
    if (cached && Date.now() < cached.expiresAt) {
      return of(cached.data);
    }
    const url: string = `/api/v1/participants/${entityType}/${entityUuid}${drive ? '?drive=true' : ''}`;
    return this.api.httpGet(url, false, {}, options).pipe(
      map((res: EntityParticipantsResponse) => {
        const data: EntityParticipant[] = (res?.data?.participants ?? [])
          .filter((p: RawParticipant) => !!p.username)
          .map((p: RawParticipant) => this.mapParticipant(p));
        CACHE_PARTICIPANTS[cacheKey] = { data, expiresAt: Date.now() + PARTICIPANTS_CACHE_TTL_MS };
        return data;
      }),
      catchError(() => of([]))
    );
  }

  private clearCache(): void {
    Object.keys(CACHE_PARTICIPANTS).forEach((key: string) => delete CACHE_PARTICIPANTS[key]);
  }

  private mapParticipant(p: RawParticipant): EntityParticipant {
    const firstName: string = p.firstName ?? '';
    const lastName: string = p.lastName ?? '';
    return {
      username: p.username,
      ...(p.accountUuid ? { accountUuid: p.accountUuid } : {}),
      firstName,
      lastName,
      fullName: (p.fullName ?? `${firstName} ${lastName}`).trim(),
      // IAM identities use the email as username; legacy consumers read either key.
      email: p.email ?? p.username,
      avatarUrl: p.avatarUrl ?? undefined,
    };
  }
}
